import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useMinWidthQuery, useMediaQuery } from "../../../../hooks/useMediaQuery";

import { MapWidgetPosition } from "./MapWidget.types";

/**
 * Helper components to position widgets on pre-fixed positions on the map.
 *
 * NOTE! position of MapWidgetPosition.HIDDEN still RENDERS the widget! It is only hidden by display: none.
 * This means any internal useEffects will execute.
 *
 */
export default function MapWidget(props: { position: MapWidgetPosition, style?: React.CSSProperties, children?: React.ReactNode, id?: string, title?: string }) {
    let style: React.CSSProperties = {}, foldButtonStyle: React.CSSProperties = {}, unfoldButtonStyle: React.CSSProperties = {};
    let writingMode: React.CSSProperties["writingMode"] = "vertical-rl";
    const [folded, setFolded] = useState<boolean>(false);

    const isSmallWidth = useMinWidthQuery();
    const isShortHeight = useMediaQuery("(max-height: 767px)");

    const topFoldButtonSpacing = isSmallWidth ? "115px" : "150px";
    const topWidgetSpacing = isSmallWidth ? "125px" : "160px";
    const widgetMaxHeight = isShortHeight ? "60vh" : "100%";

    const backgroundColor = "rgb(250, 167, 63)";

    switch (props.position) {
        case MapWidgetPosition.RIGHT:
            writingMode = "sideways-lr";
            style = {
                position: "absolute",
                top: topWidgetSpacing,
                maxHeight: widgetMaxHeight,
                right: 0
            };

            foldButtonStyle = {
                backgroundColor: backgroundColor,
                color: "white",
                position: "absolute",
                top: topFoldButtonSpacing,
                right: "0",
                borderTopLeftRadius: "5px",
                borderBottomLeftRadius: "5px",
                width: "22rem",
                height: "2rem",
                display: "flex",
                alignItems: "center",
                paddingLeft: "0.5rem",
            };

            unfoldButtonStyle = {
                backgroundColor: backgroundColor,
                color: "white",
                position: "absolute",
                top: topFoldButtonSpacing,
                right: "0",
                borderTopLeftRadius: "5px",
                borderBottomLeftRadius: "5px",
                width: "2rem",
                minHeight: "3rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: "0.4rem",
                gap: "0.4rem",
                cursor: "pointer",
            };
            break;
        case MapWidgetPosition.LEFT:
            style = {
                position: "absolute",
                top: topWidgetSpacing,
                maxHeight: widgetMaxHeight,
                left: 0
            };

            foldButtonStyle = {
                backgroundColor: backgroundColor,
                color: "white",
                position: "absolute",
                top: topFoldButtonSpacing,
                left: "0",
                borderTopRightRadius: "5px",
                borderBottomRightRadius: "5px",
                width: "22rem",
                height: "2rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: "0.5rem",
            };

            unfoldButtonStyle = {
                backgroundColor: backgroundColor,
                color: "white",
                position: "absolute",
                top: topFoldButtonSpacing,
                left: "0",
                borderTopRightRadius: "5px",
                borderBottomRightRadius: "5px",
                width: "2rem",
                minHeight: "3rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: "0.4rem",
                gap: "0.4rem",
                cursor: "pointer",
            };
            break;
        case MapWidgetPosition.TOP_HEADER:
            style = {
                position: "absolute",
                top: "10vh",
                width: "100vw",
            };
            foldButtonStyle = {
                display: "none"
            };
            unfoldButtonStyle = {
                display: "none"
            };
            break;
        case MapWidgetPosition.BOTTOM_FOOTER:
            style = {
                position: "absolute",
                bottom: "0",
            };
            foldButtonStyle = {
                backgroundColor: backgroundColor,
                color: "white",
                position: "absolute",
                borderTopLeftRadius: "5px",
                borderBottomLeftRadius: "5px",
                bottom: "5.75rem",
                right: "0",
                width: "2.5rem",
                height: "35px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            };

            unfoldButtonStyle = {
                backgroundColor: backgroundColor,
                color: "white",
                position: "absolute",
                bottom: "6rem",
                right: "0px",
                borderTopLeftRadius: "5px",
                borderBottomLeftRadius: "5px",
                width: "2rem",
                minHeight: "3rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: "0.4rem",
                gap: "0.4rem",
                cursor: "pointer",
            };
            break;
        case MapWidgetPosition.BOTTOM_RIGHT:
            writingMode = "sideways-lr";
            style = {
                position: "absolute",
                bottom: "8rem",
                right: "0",
            };
            foldButtonStyle = {
                backgroundColor: backgroundColor,
                color: "white",
                position: "absolute",
                borderTopLeftRadius: "5px",
                borderBottomLeftRadius: "5px",
                bottom: "16rem",
                right: "0",
                width: "22.5rem",
                height: "2rem",
                display: "flex",
                alignItems: "center",
                paddingLeft: "0.5rem",
            };

            unfoldButtonStyle = {
                backgroundColor: backgroundColor,
                color: "white",
                position: "absolute",
                bottom: "10vh",
                right: "0px",
                borderTopLeftRadius: "5px",
                borderBottomLeftRadius: "5px",
                width: "2rem",
                minHeight: "3rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: "0.4rem",
                gap: "0.4rem",
                cursor: "pointer",
            };
            break;
        case MapWidgetPosition.HIDDEN:
            style = {
                display: "none"
            };
            foldButtonStyle = {
                display: "none"
            };
            unfoldButtonStyle = {
                display: "none"
            };
            break;
    }
    return (
        <ErrorBoundary FallbackComponent={() => <div>Error showing widget</div>}>
            {props.position === MapWidgetPosition.HIDDEN ? null :
                folded && (
                    <div
                        role="button"
                        data-testid="map-widget-unfold-button"
                        style={unfoldButtonStyle}
                        onClick={() => setFolded(false)}
                    >
                        <FontAwesomeIcon icon={faPlus} style={{ fontSize: "0.85rem" }} />
                        {props.title && (
                            <span style={{
                                writingMode: writingMode,
                                textOrientation: "mixed",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                letterSpacing: "0.05em",
                                userSelect: "none",
                                paddingBottom: "0.4rem",
                            }}>
                                {props.title}
                            </span>
                        )}
                    </div>
                )
                ||
                <div data-testid="map-widget" className="d-flex flex-row justify-content-center">
                    <div
                        className="cems-background-color"
                        style={{
                            ...foldButtonStyle,
                            cursor: "pointer",
                        }}
                        role="button"
                        onClick={() => setFolded(true)}>
                        <FontAwesomeIcon icon={faMinus} />
                    </div>
                    <div
                        data-testid="map-widget-container"
                        id={props.id}
                        className="d-block mx-auto"
                        style={{
                            ...style,
                            ...props.style
                        }}>
                        {props.children}
                    </div>
                </div>
            }
        </ErrorBoundary>
    )
}